import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { authService } from '@/services/auth';
import { useAuthStore } from '@/context/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Lock, AlertCircle, LogOut } from 'lucide-react';

// Logo: Huella de perro (mismo que en Login)
const PawPrint = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg viewBox="0 0 32 32" fill="currentColor" className={className} style={style} xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="16" cy="22" rx="7" ry="6"/>
    <ellipse cx="7" cy="12" rx="4" ry="4.5"/>
    <ellipse cx="13" cy="6" rx="3.5" ry="4"/>
    <ellipse cx="19" cy="6" rx="3.5" ry="4"/>
    <ellipse cx="25" cy="12" rx="4" ry="4.5"/>
  </svg>
);

export default function ForceChangePassword() {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuthStore();
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [error, setError] = useState<string | null>(null);

  const changePasswordMutation = useMutation({
    mutationFn: authService.changePassword,
    onSuccess: () => {
      updateUser({ must_change_password: false });
      // Redirigir según el rol
      if (user?.role === 'superadmin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Error al cambiar la contraseña');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.new_password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    // Validar requisitos de contraseña segura
    if (!/[A-Z]/.test(formData.new_password)) {
      setError('La contraseña debe contener al menos una mayúscula');
      return;
    }
    if (!/[a-z]/.test(formData.new_password)) {
      setError('La contraseña debe contener al menos una minúscula');
      return;
    }
    if (!/\d/.test(formData.new_password)) {
      setError('La contraseña debe contener al menos un número');
      return;
    }
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~]/.test(formData.new_password)) {
      setError('La contraseña debe contener al menos un caracter especial (!@#$%...)');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('Las contraseñas no coinciden');
      return;
    }

    changePasswordMutation.mutate({
      current_password: formData.current_password,
      new_password: formData.new_password,
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex bg-cream-50">
      {/* Left side - Form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <div className="w-14 h-14 bg-gradient-to-br from-taupe-500 to-taupe-700 rounded-2xl flex items-center justify-center shadow-lg">
              <PawPrint className="w-8 h-8 text-cream-100" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-taupe-800 tracking-tight">
                <span className="text-taupe-800">ANIMA</span><span className="text-taupe-500 font-light">vet</span>
              </h1>
              <p className="text-xs text-taupe-400 tracking-wide">GESTIÓN VETERINARIA</p>
            </div>
          </div>

          {/* Title */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-taupe-800">Cambiar Contraseña</h2>
            <p className="mt-2 text-taupe-500">Por seguridad, debes establecer una nueva contraseña</p>
          </div>

          {/* Warning */}
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Cambio de contraseña requerido
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Tu contraseña actual es temporal. Debes cambiarla para continuar usando el sistema.
              </p>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded-r-lg text-sm">
                {error}
              </div>
            )}

            <Input
              label="Contraseña actual (temporal)"
              type="password"
              value={formData.current_password}
              onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
              placeholder="Ingresa la contraseña que recibiste"
              required
            />

            <div>
              <Input
                label="Nueva contraseña"
                type="password"
                value={formData.new_password}
                onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                placeholder="Crea una contraseña segura"
                required
              />
              <p className="text-xs text-taupe-500 mt-1">
                Mínimo 8 caracteres, con mayúscula, minúscula, número y caracter especial
              </p>
            </div>

            <Input
              label="Confirmar nueva contraseña"
              type="password"
              value={formData.confirm_password}
              onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
              placeholder="Repite la nueva contraseña"
              required
            />

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={handleLogout}
                className="flex-1"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Cerrar Sesión
              </Button>
              <Button
                type="submit"
                loading={changePasswordMutation.isPending}
                className="flex-1 bg-taupe-700 hover:bg-taupe-800"
              >
                <Lock className="h-4 w-4 mr-2" />
                Cambiar Contraseña
              </Button>
            </div>
          </form>

          <p className="mt-10 text-center text-sm text-taupe-400">
            © 2025 ANIMAvet. Todos los derechos reservados.
          </p>
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex lg:w-[55%] bg-gradient-to-br from-taupe-600 via-taupe-700 to-taupe-800 relative overflow-hidden">
        {/* Decorative shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cream-400/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-cream-300/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/3"></div>
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-cream-500/15 rounded-full blur-3xl"></div>
        </div>

        {/* Huellas decorativas */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <PawPrint className="absolute w-16 h-16 text-cream-300/10" style={{ top: '8%', left: '15%', transform: 'rotate(-25deg)' }} />
          <PawPrint className="absolute w-12 h-12 text-cream-300/8" style={{ top: '20%', right: '20%', transform: 'rotate(15deg)' }} />
          <PawPrint className="absolute w-20 h-20 text-cream-300/6" style={{ top: '45%', left: '8%', transform: 'rotate(-10deg)' }} />
          <PawPrint className="absolute w-14 h-14 text-cream-300/10" style={{ top: '35%', right: '10%', transform: 'rotate(30deg)' }} />
          <PawPrint className="absolute w-10 h-10 text-cream-300/8" style={{ bottom: '30%', left: '25%', transform: 'rotate(5deg)' }} />
          <PawPrint className="absolute w-18 h-18 text-cream-300/6" style={{ bottom: '15%', right: '25%', transform: 'rotate(-20deg)' }} />
          <PawPrint className="absolute w-8 h-8 text-cream-300/10" style={{ top: '60%', left: '45%', transform: 'rotate(45deg)' }} />
        </div>

        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0)`,
          backgroundSize: '32px 32px'
        }}></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center w-full p-16">
          <div className="max-w-lg">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cream-400/20 rounded-full mb-8 backdrop-blur-sm">
              <Lock className="w-4 h-4 text-cream-300" />
              <span className="text-cream-200 text-sm font-medium">Seguridad</span>
            </div>

            <h2 className="text-4xl font-bold text-cream-50 leading-tight">
              Tu seguridad es
              <span className="text-cream-300"> nuestra prioridad</span>
            </h2>

            <p className="mt-6 text-taupe-200 text-lg leading-relaxed">
              Las contraseñas temporales solo deben usarse una vez. Establece una contraseña segura que solo tú conozcas.
            </p>

            {/* Tips */}
            <div className="mt-12 p-6 bg-cream-400/10 backdrop-blur-sm rounded-2xl">
              <h3 className="text-cream-100 font-semibold mb-4">Tips para una contraseña segura:</h3>
              <ul className="space-y-3 text-taupe-300 text-sm">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-cream-400 rounded-full"></div>
                  Usa al menos 8 caracteres
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-cream-400 rounded-full"></div>
                  Combina letras, números y símbolos
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-cream-400 rounded-full"></div>
                  No uses información personal
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-cream-400 rounded-full"></div>
                  No reutilices contraseñas de otros sitios
                </li>
              </ul>
            </div>
          </div>

          {/* Huella grande decorativa */}
          <div className="absolute bottom-12 right-12">
            <PawPrint className="w-48 h-48 text-cream-200/15" />
          </div>
        </div>
      </div>
    </div>
  );
}
